import axios from "axios";

/**
 * Rails CSRF token for the session-cookie auth flow.
 *
 * Held in memory only -- never in a cookie or storage. It is fetched lazily on the first
 * mutating request and replaced whenever the session changes (login, logout, expiry).
 *
 * The token is bound to the Rails session, and /api/v2/csrf mints a session for an
 * anonymous caller, so the signed-out routes (login, password reset) can carry one too.
 * Devise rotates the token on authentication, which is why login returns its replacement
 * in the response body -- see setCsrfToken's caller in AuthService.
 */
let csrfToken: string | null = null;
let inFlight: Promise<string | null> | null = null;

// A bare axios call, deliberately not the app instance: the app instance's request
// interceptor calls back into here, which would recurse.
const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    const { data } = await axios.get<{ csrf_token: string }>("/api/v2/csrf.json", {
      baseURL: import.meta.env.VITE_BASE_API_URL,
      withCredentials: true,
      headers: { Accept: "application/json" },
    });

    return data.csrf_token ?? null;
  } catch {
    // The API is unreachable or erroring. The caller proceeds without a token and the
    // request fails on its own terms rather than being swallowed here.
    return null;
  }
};

export const ensureCsrfToken = (): Promise<string | null> => {
  if (csrfToken) {
    return Promise.resolve(csrfToken);
  }

  if (!inFlight) {
    inFlight = fetchCsrfToken()
      .then((token) => {
        csrfToken = token;
        return token;
      })
      .finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
};

export const setCsrfToken = (token: string | null | undefined) => {
  csrfToken = token ?? null;
};

export const clearCsrfToken = () => {
  csrfToken = null;
};
